import React from 'react';
import { Loader2, LogOut, RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { BsiIbConnection, BsiIbStatus } from '@/lib/banks/bsi/api';

type Props = {
  status: BsiIbStatus | null;
  connection: BsiIbConnection | null;
  loadingStatus: boolean;
  checking: boolean;
  onRefreshStatus: () => void;
  onCheckConnection: () => void;
  onLogout: () => void;
};

const BsiSessionStatusCard: React.FC<Props> = ({
  status,
  connection,
  loadingStatus,
  checking,
  onRefreshStatus,
  onCheckConnection,
  onLogout,
}) => {
  const badge = React.useMemo(() => {
    if (loadingStatus) return { label: 'در حال بررسی…', className: 'bg-slate-100 text-slate-600' };
    if (status?.session_valid) return { label: 'سشن فعال', className: 'bg-emerald-100 text-emerald-800' };
    if (status?.needs_otp) return { label: 'منتظر کد یک‌بارمصرف', className: 'bg-amber-100 text-amber-800' };
    return { label: 'وارد نشده', className: 'bg-rose-100 text-rose-800' };
  }, [loadingStatus, status]);

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-sm">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 shrink-0 text-slate-500" />
          <span className="whitespace-nowrap text-sm font-medium text-slate-800">وضعیت اتصال</span>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        </div>

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
          {status?.profile ? (
            <>
              <span className="whitespace-nowrap">
                پروفایل: {status.profile.name}
                <span className="mx-1 text-slate-300">·</span>
                {status.profile.portal_label}
              </span>
              <span className="hidden h-3 w-px bg-slate-200 sm:block" aria-hidden />
            </>
          ) : null}
          <span className="whitespace-nowrap">کاربر: {status?.username_masked || '—'}</span>
          <span className="hidden h-3 w-px bg-slate-200 sm:block" aria-hidden />
          <span className="whitespace-nowrap" dir="ltr">
            {status?.profile?.base_url?.replace(/^https?:\/\//, '') || '—'}
          </span>
          <span className="hidden h-3 w-px bg-slate-200 sm:block" aria-hidden />
          <span className="whitespace-nowrap">
            آخرین ورود:{' '}
            {status?.last_login_at ? new Date(status.last_login_at).toLocaleString('fa-IR') : '—'}
          </span>
          {status?.session_valid && (
            <>
              <span className="hidden h-3 w-px bg-slate-200 sm:block" aria-hidden />
              <span className="text-slate-500">تا {status.idle_timeout_minutes || 7} دقیقه عدم‌فعالیت</span>
            </>
          )}
          {connection && (
            <>
              <span className="hidden h-3 w-px bg-slate-200 sm:block" aria-hidden />
              <span className="truncate">{connection.message}</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Button type="button" variant="outline" size="sm" className="h-8 px-2.5" onClick={onRefreshStatus} disabled={loadingStatus}>
            {loadingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="mr-1">وضعیت</span>
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 px-2.5" onClick={onCheckConnection} disabled={checking}>
            {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            <span className="mr-1">بررسی اتصال</span>
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 px-2.5" onClick={onLogout}>
            <LogOut className="h-3.5 w-3.5" />
            <span className="mr-1">خروج سشن</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BsiSessionStatusCard;
